// client/src/components/AdminUsersViewer.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { firestore, storage } from '../../firebase';
import {
  deleteDoc, doc, setDoc, getDocs, getDoc, collection,
  query, where, limit, updateDoc, addDoc, collectionGroup, deleteField
} from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getMetadata, uploadBytes } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import AdminNavbar from './AdminNavbar';
import '../CSS/Dashboard.css';
import emailjs from 'emailjs-com';
import { QRCodeCanvas } from 'qrcode.react';

const QR_BACKGROUND_STANDARD = "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2FMODEL%20QRC%20V4%20regulier.png?alt=media&token=e718eb31-c136-4da9-bdc3-fa152f6f3a9f";
const QR_BACKGROUND_NFC = "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2FMODEL%20QRC%20V4%20nfc.png?alt=media&token=7080393c-661f-47ec-a5fb-61dafcf89b57";

/** Devise normalisée parmi celles autorisées */
const ALLOWED_CURRENCIES = ['EUR', 'USD', 'CAD', 'GBP', 'CHF', 'XOF'];
const normalizeCurrency = (cur) => {
  if (!cur) return 'EUR';
  const s = String(cur).trim().toUpperCase();
  if (s === 'CFA' || s === 'FCFA') return 'XOF';
  return ALLOWED_CURRENCIES.includes(s) ? s : 'EUR';
};


const normalizeUsedEntries = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  return [raw];
};

const buildAffiliateLinkPayload = async (codeDocSnap, matchEntry) => {
  const data = codeDocSnap.data() || {};
  const promoDocRef = codeDocSnap.ref?.parent?.parent || null;
  let promoData = {};
  if (promoDocRef) {
    try {
      const promoSnap = await getDoc(promoDocRef);
      if (promoSnap.exists()) {
        promoData = promoSnap.data() || {};
      }
    } catch (error) {
      console.error('Erreur récupération doc affiliate promo :', error);
    }
  }
  let affiliateEmail = '';
  if (promoDocRef) {
    try {
      const affiliateUserSnap = await getDoc(doc(firestore, 'users', promoDocRef.id));
      if (affiliateUserSnap.exists()) {
        affiliateEmail = affiliateUserSnap.data()?.email || '';
      }
    } catch (error) {
      console.error('Erreur récupération email affilié :', error);
    }
  }
  return {
    affiliateId: promoDocRef?.id || '',
    affiliatePseudo: promoData.pseudo || promoDocRef?.id || '',
    affiliateEmail,
    codeId: codeDocSnap.id,
    promoCode: data.promoCode || '',
    codeCurrency: normalizeCurrency(data.currency || 'EUR'),
    codePercentage: Number(data.percentage) || 0,
    usedEntry: matchEntry || null,
  };
};

const findAffiliateLinkInDocs = async (docs, uid) => {
  for (const docSnap of docs) {
    const data = docSnap.data() || {};
    // Vérifier ancien tableau usedTimestamps (rétro-compat)
    const entries = normalizeUsedEntries(data.usedTimestamps);
    const match = entries.find(entry => entry?.uid === uid);
    if (match) {
      return await buildAffiliateLinkPayload(docSnap, match);
    }
    // Vérifier nouvelle sous-collection activations
    try {
      const activSnap = await getDocs(query(collection(docSnap.ref, 'activations'), where('uid', '==', uid), limit(1)));
      if (!activSnap.empty) {
        const activData = activSnap.docs[0].data();
        return await buildAffiliateLinkPayload(docSnap, { ...activData, __activationId: activSnap.docs[0].id });
      }
    } catch {}
  }
  return null;
};

const formatDateTimeForLog = (date = new Date()) =>
  date.toLocaleString('fr-FR', { timeZone: 'America/Toronto', timeZoneName: 'short' });

const AdminUsersViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [canGrantAccess, setCanGrantAccess] = useState(false);
  const [canResignAccess, setCanResignAccess] = useState(false);

  // NEW — états pour affiliates
  const [canActivateAffiliate, setCanActivateAffiliate] = useState(false);
  const [canResignAffiliate, setCanResignAffiliate] = useState(false);

  // Photo de profil
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);

  // === REGIE: état de mise à jour du rôle (pour désactiver le clic pendant l'update)
  const [updatingRegie, setUpdatingRegie] = useState(false);

  // --- Styles nécessaires pour garder l’aspect des boutons + modale (identiques à AdminDashboard) ---
  const modalAndBtnStyles = `
    .auv-page { width: 100%; display: flex; flex-direction: column; gap: 20px; }
    .auv-page .eb-page-header { margin-bottom: 0; align-items: center; }
    .dashboard-container .auv-page .eb-page-title { margin: 0; text-align: left; }

    .auv-card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); padding: 20px 24px; min-width: 0; }
    .dashboard-container .auv-card__title { margin: 0 0 14px; font-size: 16px; font-weight: 650; color: var(--eb-text); text-align: left; }

    /* Identité */
    .auv-identity { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
    .auv-avatar { width: 72px; height: 72px; object-fit: cover; border-radius: 50%; border: 1px solid var(--eb-border); flex-shrink: 0; }
    .auv-identity__main { flex: 1 1 200px; min-width: 0; }
    .auv-identity__name { margin: 0; font-size: 18px; font-weight: 650; color: var(--eb-text); overflow-wrap: anywhere; }
    .auv-identity__meta { margin: 4px 0 0; font-size: 13.5px; color: var(--eb-muted); overflow-wrap: anywhere; display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .auv-mono { font-family: var(--eb-font-mono); font-size: 12.5px; }

    .auv-toolbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
    .auv-qr { display: flex; justify-content: center; padding: 8px 0 16px; }

    /* Champs */
    .auv-fields { display: flex; flex-direction: column; }
    .auv-field { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-bottom: 1px solid var(--eb-border); border-radius: 0; }
    .auv-field:last-child { border-bottom: 0; }
    .auv-field:hover { background: var(--eb-surface-2); }
    .auv-field--highlight { background: var(--eb-warning-bg); border-radius: var(--eb-radius-sm); }
    .auv-field--highlight:hover { background: var(--eb-warning-bg); }
    .auv-field__body { flex: 1; min-width: 0; display: grid; grid-template-columns: minmax(140px, 220px) 1fr; gap: 12px; align-items: center; }
    .auv-field__key { font-family: var(--eb-font-mono); font-size: 12.5px; color: var(--eb-muted); font-weight: 600; overflow-wrap: anywhere; }
    .auv-field__val { font-size: 14px; color: var(--eb-text); overflow-wrap: anywhere; white-space: pre-wrap; }
    .auv-field__val input { width: 100%; min-height: 38px; padding: 0 10px; font-size: 14px; }
    .auv-field__actions { display: flex; gap: 6px; flex-shrink: 0; }

    .auv-actions { display: flex; flex-wrap: wrap; gap: 10px; }
    .auv-actions .button-delete { margin-left: auto; }

    .btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 40px; padding: 0 14px; border-radius: var(--eb-radius-sm); border: 1px solid var(--eb-border-strong); background: var(--eb-surface); color: var(--eb-text-2); font-weight: 600; font-size: 14px; cursor: pointer; box-shadow: var(--eb-shadow-xs); transition: background-color .15s ease; }
    .btn:hover:not(:disabled) { background: var(--eb-surface-2); color: var(--eb-text); }
    .btn:disabled { opacity: .55; cursor: not-allowed; }
    .btn-open { }
    .btn-import { background: var(--eb-brand); border-color: var(--eb-brand); color: #fff; }
    .btn-import:hover:not(:disabled) { background: var(--eb-brand-700); color: #fff; }

    /* Overlay plein écran */
    .modal-overlay {
      position: fixed; inset: 0;
      background: rgba(15,35,56,.55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center;
      z-index: 1000;
      padding: 16px;
    }

    /* Contenu scrollable, compatible iOS */
    .modal-content {
      width: 100%; max-width: 640px;
      background: var(--eb-surface); border-radius: var(--eb-radius-xl); padding: 24px;
      position: relative;
      box-shadow: var(--eb-shadow-lg);
      max-height: 90vh; max-height: 90dvh;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      display: flex; flex-direction: column; gap: 4px;
      text-align: left;
    }
    .dashboard-container .modal-content h2 { font-size: 19px; font-weight: 650; margin: 0 0 8px; text-align: left; padding-right: 40px; }
    .dashboard-container .modal-content h3 { font-size: 16px; font-weight: 650; margin: 12px 0 6px; text-align: left; padding-right: 40px; }
    .modal-content p { margin: 4px 0; color: var(--eb-text-2); font-size: 14.5px; }
    .modal-content select { width: 100%; min-height: 44px; padding: 0 12px; font-size: 14.5px; margin: 6px 0; }

    /* Croix toujours visible même en scroll */
    .modal-close {
      position: sticky; top: 0; align-self: flex-end; margin-bottom: -36px;
      width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center;
      background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-sm);
      color: var(--eb-muted); font-size: 22px; line-height: 1; cursor: pointer; z-index: 1;
    }
    .modal-close:hover { background: var(--eb-surface-2); color: var(--eb-text); }

    .modal-actions { display: flex; gap: 10px; justify-content: center; margin-top: 16px; flex-wrap: wrap; }
    .modal-actions + .modal-actions { margin-top: 8px; padding-top: 12px; border-top: 1px solid var(--eb-border); }
    .docs-grid { display: grid; grid-template-columns: 1fr; gap: 12px; margin-top: 8px; }
    @media (min-width: 700px) { .docs-grid { grid-template-columns: 1fr 1fr; } }
    .doc-block { border: 1px solid var(--eb-border); border-radius: var(--eb-radius); padding: 14px; background: var(--eb-surface-2); }
    .doc-block strong { font-size: 13.5px; color: var(--eb-text-2); }
    .doc-block em { color: var(--eb-muted); font-style: normal; font-size: 13.5px; }

    /* Images contenues et responsives */
    .thumb {
      display: block;
      width: 100%;
      height: auto;
      max-height: 65vh;
      object-fit: contain;
      border-radius: var(--eb-radius-sm); border: 1px solid var(--eb-border); background: var(--eb-surface);
    }

    .doc-links a { display: inline-block; margin-right: 12px; margin-bottom: 6px; font-size: 14px; font-weight: 500; word-break: break-word; }
    .inline-note { font-size: 14px; color: var(--eb-muted); }
    .row { display: flex; gap: 10px; align-items: center; }
    .input { min-height: 44px; padding: 0 12px; border: 1px solid var(--eb-border-strong); border-radius: var(--eb-radius-sm); flex: 1; min-width: 0; font-size: 15px; }
    .select { min-height: 44px; padding: 0 12px; border: 1px solid var(--eb-border-strong); border-radius: var(--eb-radius-sm); font-size: 14.5px; }
    .actions { display: flex; gap: 10px; justify-content: center; margin-top: 16px; flex-wrap: wrap; }
    .btn-primary { display: inline-flex; align-items: center; justify-content: center; min-height: 42px; background: var(--eb-brand); color: #fff; border: 1px solid transparent; border-radius: var(--eb-radius-sm); padding: 0 18px; font-weight: 600; font-size: 14.5px; cursor: pointer; box-shadow: var(--eb-shadow-xs); }
    .btn-primary:hover:not(:disabled) { background: var(--eb-brand-700); }
    .btn-primary:disabled { opacity: .55; cursor: not-allowed; }
    .btn-ghost { display: inline-flex; align-items: center; justify-content: center; min-height: 42px; background: var(--eb-surface); color: var(--eb-text-2); border: 1px solid var(--eb-border-strong); border-radius: var(--eb-radius-sm); padding: 0 18px; font-weight: 600; font-size: 14.5px; cursor: pointer; box-shadow: var(--eb-shadow-xs); }
    .btn-ghost:hover:not(:disabled) { background: var(--eb-surface-2); color: var(--eb-text); }
    .btn-ghost:disabled { opacity: .55; cursor: not-allowed; }
    .hint { margin-top: 12px; padding: 12px 14px; background: var(--eb-surface-2); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-sm); color: var(--eb-text-2); font-size: 14px; line-height: 1.5; }
    .hint em { color: var(--eb-muted); }
    .interval-select { margin-top: 14px; display: flex; flex-direction: column; gap: 6px; }
    .interval-select > span { font-size: 13.5px; color: var(--eb-text-2); }

    /* Petit préloader avant d'afficher la belle UI */
    .ugly-splash { background: var(--eb-surface-2); border-radius: var(--eb-radius); padding: 18px; border: 1px dashed var(--eb-border-strong); }

    /* === REGIE: styles de la switch + conteneur === */
    .regie-inline { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .switch-wrap { display:flex; align-items:center; gap:10px; min-height: 40px; padding: 0 12px; border:1px solid var(--eb-border); border-radius: var(--eb-radius-sm); background: var(--eb-surface-2); }
    .switch-title { font-weight:600; font-size: 14px; color: var(--eb-text-2); }
    .toggle { position:relative; flex-shrink: 0; width:44px; height:24px; background: var(--eb-border-strong); border-radius:999px; transition:background .2s ease; cursor:pointer; }
    .toggle:after { content:""; position:absolute; top:3px; left:3px; width:18px; height:18px; background:#fff; border-radius:50%; box-shadow: var(--eb-shadow-sm); transition:transform .2s ease; }
    .toggle.checked { background: var(--eb-success); }
    .toggle.checked:after { transform: translateX(20px); }

    @media (max-width: 600px) {
      .auv-card { padding: 16px; }
      .auv-field { flex-direction: column; align-items: stretch; gap: 8px; padding: 12px 4px; }
      .auv-field__body { grid-template-columns: 1fr; gap: 2px; }
      .auv-field__actions { justify-content: flex-end; }
      .auv-actions > button, .auv-actions .button-delete { flex: 1 1 100%; margin-left: 0; }
      .modal-content { padding: 20px 16px; }
      .modal-actions > button, .actions > button { flex: 1 1 100%; }
      .row { flex-wrap: wrap; }
    }
  `;

  // ---- NEW: Modale documents + import contrat (affiliates / affiliatepending) ----
  const [docsModalOpen, setDocsModalOpen] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsPreview, setDocsPreview] = useState({ photo: null, kbis: [], rib: [], contrat: [] });
  const [selectedUid, setSelectedUid] = useState(null);
  const [uploadingUid, setUploadingUid] = useState(null);

  // ---- NEW: Modale de saisie montant/devise pour l’activation ----
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('EUR');
  const [paymentInterval, setPaymentInterval] = useState('mensuel');
  const [pendingAction, setPendingAction] = useState(null); // { type: 'affiliate'|'normal', sendEmail?: boolean }
  const [suggestedAmount, setSuggestedAmount] = useState(null);
  const [annualPreview, setAnnualPreview] = useState(null);
  const [factorLabel, setFactorLabel] = useState('—');
  const [useFullAmount, setUseFullAmount] = useState(false);

  // 🔒 Bloque le scroll de l’arrière-plan quand une modale est ouverte (iOS compris)
  useEffect(() => {
    const anyOpen = docsModalOpen || paymentModalOpen || showAffiliateModal;
    if (!anyOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [docsModalOpen, paymentModalOpen, /* eslint-disable-line react-hooks/exhaustive-deps */]);

  const getLatestInFolder = async (folderPath) => {
    try {
      const folderRef = ref(storage, folderPath);
      const { items } = await listAll(folderRef);
      if (!items?.length) return null;
      const withTimestamps = await Promise.all(
        items.map(async (it) => {
          try {
            const meta = await getMetadata(it);
            return { it, ts: new Date(meta.timeCreated).getTime() || 0 };
          } catch {
            return { it, ts: 0 };
          }
        })
      );
      withTimestamps.sort((a, b) => b.ts - a.ts);
      return await getDownloadURL(withTimestamps[0].it);
    } catch {
      return null;
    }
  };
  const getAllInFolder = async (folderPath) => {
    try {
      const folderRef = ref(storage, folderPath);
      const { items } = await listAll(folderRef);
      const urls = await Promise.all(items.map(it => getDownloadURL(it)));
      return urls;
    } catch {
      return [];
    }
  };
  const openDocsModal = async (uid) => {
    setSelectedUid(uid);
    setDocsModalOpen(true);
    setDocsLoading(true);
    const [photoUrl, kbisUrls, ribUrls, contratUrls] = await Promise.all([
      getLatestInFolder(`${uid}/photo-profil`),
      getAllInFolder(`${uid}/documents/kbis`),
      getAllInFolder(`${uid}/documents/rib`),
      getAllInFolder(`${uid}/documents/contrat`),
    ]);
    setDocsPreview({ photo: photoUrl, kbis: kbisUrls, rib: ribUrls, contrat: contratUrls });
    setDocsLoading(false);
  };
  const closeDocsModal = () => {
    setDocsModalOpen(false);
    setSelectedUid(null);
    setDocsPreview({ photo: null, kbis: [], rib: [], contrat: [] });
  };
  const handleImportSignedContract = async (uid, file, inputEl) => {
    if (!file) return;
    try {
      setUploadingUid(uid);
      const destRef = ref(storage, `${uid}/documents/contrat/${Date.now()}_${file.name}`);
      await uploadBytes(destRef, file);
      alert('Contrat signé importé avec succès.');
    } catch (e) {
      console.error('Erreur upload contrat signé :', e);
      alert("Erreur lors de l'import du contrat signé.");
    } finally {
      setUploadingUid(null);
      if (inputEl) inputEl.value = '';
      if (docsModalOpen && selectedUid === uid) {
        setDocsLoading(true);
        const contratUrls = await getAllInFolder(`${uid}/documents/contrat`);
        setDocsPreview(prev => ({ ...prev, contrat: contratUrls }));
        setDocsLoading(false);
      }
    }
  };

  // Modale affiliation
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [modalStep, setModalStep] = useState('confirm');
  const [validationOrigin, setValidationOrigin] = useState('');
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateEmail, setAffiliateEmail] = useState('');
  const [allCodes, setAllCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [previousAffiliateLink, setPreviousAffiliateLink] = useState(null);
  const [checkingAffiliateHistory, setCheckingAffiliateHistory] = useState(false);
  const [reactivationModalOpen, setReactivationModalOpen] = useState(false);
  const [reactivationPendingLink, setReactivationPendingLink] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  const user = location.state?.user;

  // Fiche user
  const [userDetails, setUserDetails] = useState(user);
  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState('');

  // ShortID
  const [shortId, setShortId] = useState('');

  const touchAffiliateCodeParent = useCallback(async (affiliateId) => {
    if (!affiliateId) return;
    try {
      await updateDoc(doc(firestore, 'code promo', affiliateId), {
        codesUpdatedAt: new Date(),
      });
    } catch (error) {
      console.error('Erreur mise à jour codesUpdatedAt affilié :', error);
    }
  }, []);

  const upsertAffiliateUsageEntry = useCallback(async (link, transformer) => {
    if (!link?.affiliateId || !link.codeId || !user?.id) return null;
    try {
      const codeDocRef = doc(firestore, 'code promo', link.affiliateId, 'codes', link.codeId);
      const activationsRef = collection(codeDocRef, 'activations');
      // Chercher dans la sous-collection activations (nouvelle structure)
      const activSnap = await getDocs(query(activationsRef, where('uid', '==', user.id), limit(1)));
      if (!activSnap.empty) {
        const activDoc = activSnap.docs[0];
        const updatedEntry = transformer({ ...activDoc.data() });
        await updateDoc(activDoc.ref, updatedEntry);
        await touchAffiliateCodeParent(link.affiliateId);
        return updatedEntry;
      }
      // Rétro-compat : chercher dans l'ancien tableau usedTimestamps
      const codeSnap = await getDoc(codeDocRef);
      const existingData = codeSnap.exists() ? codeSnap.data() : {};
      const existingEntries = normalizeUsedEntries(existingData?.usedTimestamps);
      let updatedEntry = null;
      const updatedEntries = existingEntries.map(entry => {
        if (!entry?.uid || entry.uid !== user.id) return entry;
        const nextEntry = transformer({ ...entry });
        updatedEntry = nextEntry;
        return nextEntry;
      });
      if (!updatedEntry) {
        // Aucune entrée existante — créer dans la sous-collection
        updatedEntry = transformer({ ...(link.usedEntry || {}), uid: user.id });
        await addDoc(activationsRef, updatedEntry);
      } else {
        await updateDoc(codeDocRef, { usedTimestamps: updatedEntries });
      }
      await touchAffiliateCodeParent(link.affiliateId);
      return updatedEntry;
    } catch (error) {
      console.error('Erreur mise à jour entrée affilié :', error);
      return null;
    }
  }, [touchAffiliateCodeParent, user?.id]);

  const logAffiliateReactivationEntry = useCallback(async (link) => {
    const now = new Date();
    const entry = await upsertAffiliateUsageEntry(link, (entryData) => {
      const recordedAmount = entryData.recordedAmount ?? link?.usedEntry?.recordedAmount ?? entryData.grossAmount;
      const currency = entryData.currency ?? link?.usedEntry?.currency ?? 'EUR';
      return {
        ...entryData,
        uid: user.id,
        datetime: formatDateTimeForLog(now),
        reactivatedAt: now.toISOString(),
        status: 'reactivated',
        recordedAmount,
        currency,
      };
    });
    if (entry) {
      setPreviousAffiliateLink(prev => (prev ? { ...prev, usedEntry: entry } : prev));
    }
  }, [upsertAffiliateUsageEntry, user?.id]);

  const fetchAffiliateLinkForUser = useCallback(async () => {
    if (!userDetails?.id) return null;
    const uid = userDetails.id;
    const codeValue = (userDetails.codepromo || '').toString().trim();

    // Recherche ciblée uniquement si le user a un code promo enregistré
    if (!codeValue) return null;

    try {
      const targetedQuery = query(
        collectionGroup(firestore, 'codes'),
        where('promoCode', '==', codeValue)
      );
      const targetedSnap = await getDocs(targetedQuery);
      return await findAffiliateLinkInDocs(targetedSnap.docs, uid);
    } catch (error) {
      console.error('Erreur lors de la recherche ciblée du code promo précédent :', error);
      return null;
    }
  }, [userDetails?.id, userDetails?.codepromo]);

  // 🔥 FIX #1 : relecture fraîche du user au montage / refresh (merge l'id)
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const uRef = doc(firestore, 'users', user.id);
        const snap = await getDoc(uRef);
        if (snap.exists()) {
          const data = snap.data();
          setUserDetails(prev => ({ ...(prev || {}), ...data, id: user.id }));
        }
      } catch (e) {
        console.error('Erreur lors du rafraîchissement du user:', e);
      }
    })();
  }, [user?.id]);

  // Récupération photo + éligibilité
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      try {
        const folderRef = ref(storage, `${user.id}/photo-profil`);
        const result = await listAll(folderRef);
        if (result.items.length === 0) return;
        const itemsWithMetadata = await Promise.all(
          result.items.map(async (item) => {
            const metadata = await getMetadata(item);
            return { item, timeCreated: new Date(metadata.timeCreated) };
          })
        );
        itemsWithMetadata.sort((a, b) => b.timeCreated - a.timeCreated);
        const latestItem = itemsWithMetadata[0].item;
        const url = await getDownloadURL(latestItem);
        setProfilePhotoUrl(url);
      } catch (error) {
        console.error("Erreur lors de la récupération de la photo de profil :", error);
      }
    };

    const checkAccessEligibility = async () => {
      // Commerçants
      if (user.role === 'commercant' || user.role === 'commercantpending') {
        const entrepriseRef = collection(firestore, 'entreprises');
        const qy = query(entrepriseRef, where('userId', '==', user.id));
        const querySnapshot = await getDocs(qy);

        if (querySnapshot.empty) {
          setCanGrantAccess(true);
          setCanResignAccess(false);
        } else {
          const entrepriseData = querySnapshot.docs[0].data();
          if (entrepriseData.base !== 'active') {
            setCanGrantAccess(true);
            setCanResignAccess(false);
          } else {
            setCanGrantAccess(false);
            setCanResignAccess(true);
          }
        }
        setCanActivateAffiliate(false);
        setCanResignAffiliate(false);
        return;
      }

      // Affiliates
      if (user.role === 'affiliatepending' || user.role === 'affiliates') {
        const uRef = doc(firestore, 'users', user.id);
        const uSnap = await getDoc(uRef);
        const uData = uSnap.exists() ? uSnap.data() : {};
        const status = uData?.Status;

        if (status === 'active') {
          setCanActivateAffiliate(false);
          setCanResignAffiliate(true);
        } else {
          setCanActivateAffiliate(true);
          setCanResignAffiliate(false);
        }

        setCanGrantAccess(false);
        setCanResignAccess(false);
        return;
      }

      // Autres rôles
      setCanGrantAccess(false);
      setCanResignAccess(false);
      setCanActivateAffiliate(false);
      setCanResignAffiliate(false);
    };

    if (userDetails) {
      fetchProfilePhoto();
      checkAccessEligibility();
    }
  }, [userDetails, user?.id, user?.role]);

  // Récupérer ShortID
  useEffect(() => {
    const fetchShortId = async () => {
      if (!userDetails?.id) return;
      try {
        const fidRef = doc(firestore, 'fidelity', userDetails.id);
        const fidSnap = await getDoc(fidRef);
        if (fidSnap.exists()) {
          setShortId(fidSnap.data()?.ShortID || '');
        } else {
          setShortId('');
        }
      } catch (e) {
        console.error('Erreur lors de la récupération du ShortID :', e);
        setShortId('');
      }
    };
    fetchShortId();
  }, [userDetails?.id]);

  useEffect(() => {
    if (!userDetails?.id) {
      setPreviousAffiliateLink(null);
      setCheckingAffiliateHistory(false);
      return;
    }
    let isActive = true;
    setCheckingAffiliateHistory(true);
    (async () => {
      try {
        const link = await fetchAffiliateLinkForUser();
        if (isActive) {
          setPreviousAffiliateLink(link);
        }
      } catch (error) {
        console.error('Erreur lors de la détection du lien affilié historique :', error);
        if (isActive) {
          setPreviousAffiliateLink(null);
        }
      } finally {
        if (isActive) {
          setCheckingAffiliateHistory(false);
        }
      }
    })();
    return () => {
      isActive = false;
    };
  }, [fetchAffiliateLinkForUser, userDetails?.id]);

  useEffect(() => {
    if (!paymentModalOpen) return;
    const raw = String(paymentAmount).replace(',', '.').trim();
    const num = Number(raw);
    if (!raw || isNaN(num) || num <= 0) {
      setSuggestedAmount(null);
      setAnnualPreview(null);
      return;
    }
    const factor = useFullAmount ? 1 : computeFactor();
    const cur = normalizeCurrency(paymentCurrency);
    const recorded = roundByCurrency(num * factor, cur);
    setSuggestedAmount(recorded);

    const intervalValue = typeof paymentInterval === 'string' ? paymentInterval.toLowerCase() : 'mensuel';
    const isAnnual = intervalValue.startsWith('ann');
    if (isAnnual) {
      const recurringMonthly = roundByCurrency(num * 0.35, cur);
      const recurringMonthsCount = 9;
      const month2Preview = roundByCurrency(num * computeFactor(), cur);
      const firstTwo = roundByCurrency(recorded + month2Preview, cur);
      const recurringTotal = roundByCurrency(recurringMonthly * recurringMonthsCount, cur);
      const total = roundByCurrency(firstTwo + recurringTotal, cur);
      setAnnualPreview({
        total,
        firstTwo,
        recurringMonthly,
        recurringMonthsCount,
        monthlyRecorded: recorded,
        currency: cur,
      });
    } else {
      setAnnualPreview(null);
    }
    // eslint-disable-next-line
  }, [paymentModalOpen, paymentAmount, paymentCurrency, paymentInterval, selectedCode, useFullAmount]);

  /** ------------------------ NOUVELLE LOGIQUE DE CAPTURE MONTANT/DEVISE ------------------------ **/
  const computeFactor = (codeContext = selectedCode) => {
    // Règle: si le code promo a "percentage === 10" (ancien -10%), on enregistre 50%, sinon 55%
    if (codeContext && Number(codeContext.discount) === 10) return 0.50;
    return 0.55;
  };

  // round helper: XOF sans décimales, sinon 2 décimales
  const roundByCurrency = (value, cur) => {
    if (cur === 'XOF') return Math.round(value);
    return Math.round(value * 100) / 100;
  };

  const formatCurrency = (amount, cur) => {
    const currency = normalizeCurrency(cur);
    const n = Number(amount || 0);
    const formatter = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: currency === 'XOF' ? 0 : 2,
      maximumFractionDigits: currency === 'XOF' ? 0 : 2,
    });
    return formatter.format(currency === 'XOF' ? Math.round(n) : n);
  };

  // Devise pré-sélectionnée : user > param > code > EUR
  const openPaymentModal = (preselectCurrency, options = {}) => {
    const codeContext = options.selectedCode || selectedCode;
    const savedUserCurrency = normalizeCurrency(userDetails?.currencyCode || '');
    const chosen = savedUserCurrency
      || normalizeCurrency(preselectCurrency || '')
      || (codeContext?.currency ? normalizeCurrency(codeContext.currency) : 'EUR');
    const savedIntervalRaw = (userDetails?.subscriptionInterval || '').toString().toLowerCase();
    const normalizedInterval = savedIntervalRaw.startsWith('ann') ? 'annuel' : 'mensuel';

    setPaymentInterval(normalizedInterval);
    setPaymentCurrency(chosen);
    setPaymentAmount('');
    const factor = computeFactor(codeContext);
    setFactorLabel(`${Math.round(factor * 100)}%`);
    setSuggestedAmount(null);
    setAnnualPreview(null);
    setUseFullAmount(false);
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
  };

  // Étape après saisie du montant : on passe au choix "avec/sans email"
  const continueToValidationChoice = () => {
    if (suggestedAmount === null) {
      alert('Veuillez saisir un montant valide.');
      return;
    }
    setPaymentModalOpen(false);
    setValidationOrigin(modalStep); // mémorise d’où on vient
    setModalStep('validateChoice');
    setShowAffiliateModal(true);
  };

  // Finalise (traçage + activation + éventuels emails selon choix)
  const confirmPaymentAndProceed = async () => {
    const raw = String(paymentAmount).replace(',', '.').trim();
    const num = Number(raw);
    if (!raw || isNaN(num) || num <= 0) {
      alert('Veuillez saisir un montant valide.');
      return;
    }
    const cur = normalizeCurrency(paymentCurrency);
    // grossAmount = plein montant avant remise (num peut être le prix remisé si un code de réduction est appliqué)
    const discountPct = selectedCode ? Number(selectedCode.discount ?? 0) : 0;
    const grossAmount = discountPct > 0 ? roundByCurrency(num / (1 - discountPct / 100), cur) : num;
    const factor = useFullAmount ? 1 : computeFactor();
    const recordedAmount = roundByCurrency(num * factor, cur);
    const baseMonthFactor = computeFactor(); // 55% ou 50%, indépendant du bouton "plein montant"
    const month1Amount = useFullAmount ? grossAmount : roundByCurrency(grossAmount * baseMonthFactor, cur);
    const month2Amount = roundByCurrency(grossAmount * baseMonthFactor, cur);
    const recurringAmount = roundByCurrency(grossAmount * 0.35, cur);
    const now = new Date();
    const intervalValue = typeof paymentInterval === 'string' ? paymentInterval.toLowerCase() : 'mensuel';
    const interval = intervalValue.startsWith('ann') ? 'annuel' : 'mensuel';
    const intervalLabel = interval === 'annuel' ? 'Abonnement annuel' : 'Abonnement mensuel';

    try {
      const sendEmail = !!(pendingAction?.sendEmail);
      const userRef = doc(firestore, 'users', user.id);

      await updateDoc(userRef, {
        subscriptionInterval: interval,
        subscriptionIntervalUpdatedAt: now
      });

      // 1) Accorder l’accès (et potentiels emails selon sendEmail)
      await grantAccessCommon(sendEmail);

      // 2) Tracer la transaction
      if (pendingAction?.type === 'affiliate' && selectedCode) {
        const { affiliateId, id: codeId } = selectedCode;

        // Vérifier duplication pour ce user (sous-collection + rétro-compat ancien tableau)
        const codesCollectionRef = collection(firestore, 'code promo', affiliateId, 'codes');
        const codesSnapshot = await getDocs(codesCollectionRef);
        let userAlreadyExists = false;
        for (const codeDoc of codesSnapshot.docs) {
          const codeData = codeDoc.data();
          // Vérifier ancien tableau
          const arr = Array.isArray(codeData.usedTimestamps) ? codeData.usedTimestamps : [];
          if (arr.some(entry => entry.uid === user.id)) { userAlreadyExists = true; break; }
          // Vérifier sous-collection activations
          const actSnap = await getDocs(query(collection(codeDoc.ref, 'activations'), where('uid', '==', user.id), limit(1)));
          if (!actSnap.empty) { userAlreadyExists = true; break; }
        }

        if (!userAlreadyExists) {
          const codeDocRef = doc(firestore, 'code promo', affiliateId, 'codes', codeId);
          const activationsRef = collection(codeDocRef, 'activations');
          const timestampEntry = {
            uid: user.id,
            datetime: now.toLocaleString('fr-FR', {
              timeZone: 'America/Toronto',
              timeZoneName: 'short'
            }),
            grossAmount,
            recordedAmount,
            month1Amount,
            month2Amount,
            recurringAmount,
            currency: cur,
            factorApplied: `${Math.round(factor * 100)}%`,
            billingInterval: interval,
          };
          await addDoc(activationsRef, timestampEntry);
          const codeSnap2 = await getDoc(codeDocRef);
          const prevCount = Number(codeSnap2.data()?.activationsCount) || 0;
          await updateDoc(codeDocRef, { activationsCount: prevCount + 1 });

          setMessage(
            sendEmail
              ? `Accès accordé (${intervalLabel}), transaction enregistrée et emails envoyés.`
              : `Accès accordé (${intervalLabel}), transaction enregistrée (aucun email envoyé).`
          );
        } else {
          setMessage(`Accès accordé (${intervalLabel}). Déjà activé par cet affilié, pas de nouvelle entrée transactionnelle.`);
        }
      } else {
        // Sans affilié: trace locale sur le user
        const sub = collection(firestore, 'users', user.id, 'activations');
        await addDoc(sub, {
          datetime: now.toLocaleString('fr-FR', {
            timeZone: 'America/Toronto',
            timeZoneName: 'short'
          }),
          grossAmount,
          recordedAmount,
          month1Amount,
          month2Amount,
          recurringAmount,
          currency: cur,
          factorApplied: `${Math.round(factor * 100)}%`,
          billingInterval: interval,
          origin: 'no-affiliate'
        });
        setMessage(
          pendingAction?.sendEmail
            ? `Accès accordé (${intervalLabel}), transaction enregistrée et emails envoyés.`
            : `Accès accordé (${intervalLabel}), transaction enregistrée (aucun email envoyé).`
        );
      }

      // rafraîchir fiche user
      const updatedUserDoc = await getDoc(userRef);
      if (updatedUserDoc.exists()) {
        // 🔥 FIX #2 (partie A) : conserver l'id
        setUserDetails({ id: user.id, ...updatedUserDoc.data() });
      }
    } catch (error) {
      console.error('Erreur lors de la validation transactionnelle :', error);
      setMessage('Erreur lors de la validation/traçage de la transaction.');
    } finally {
      setShowAffiliateModal(false);
      setModalStep('confirm');
      setSelectedAffiliate(null);
      setSelectedCode(null);
      setPendingAction(null);
    }
  };

  /**
   * Accorder l'accès (entreprise active) + email optionnel
   * IMPORTANT: si sendEmail === false => AUCUN email n’est envoyé.
   */
  const grantAccessCommon = async (sendEmail = true) => {
    const entrepriseRef = collection(firestore, 'entreprises');
    const qy = query(entrepriseRef, where('userId', '==', user.id));
    const querySnapshot = await getDocs(qy);

    if (!querySnapshot.empty) {
      const entrepriseDoc = querySnapshot.docs[0].ref;
      await updateDoc(entrepriseDoc, {
        base: 'active',
        promotion: 'active',
        fidelity: 'inactive',
      });
    } else {
      const newEntrepriseId = uuidv4();
      const entrepriseRefDoc = doc(firestore, 'entreprises', newEntrepriseId);
      const newEntrepriseData = {
        createdAt: new Date(),
        createdBy: user.email,
        email: user.email,
        userId: user.id,
        pseudo: user.pseudo,
        base: 'active',
        promotion: 'active',
        fidelity: 'inactive',
        satisfaction: 'active',
      };
      await setDoc(entrepriseRefDoc, newEntrepriseData);
    }
    if (user.role === 'commercantpending') {
      await updateDoc(doc(firestore, 'users', user.id), { role: 'commercant' });
    }

    // ---- EMAILS UNIQUEMENT si sendEmail === true
    if (sendEmail) {
      const templateParams = {
        company: user.pseudo,
        to_email: user.email,                 // client
        promo_code: selectedCode ? selectedCode.promoCode : '',
        bcc_email: affiliateEmail || ''       // affiliate (BCC)
      };

      await emailjs.send(
        process.env.REACT_APP_EMAIL_SERVICE_ID,
        process.env.REACT_APP_EMAIL_TEMPLATE2_ID,
        templateParams,
        process.env.REACT_APP_EMAIL_USER_ID
      );
    }
  };

  /** -------------------- OUVERTURE DU PARCOURS D’ACTIVATION -------------------- **/
  // Normal (sans affiliation) => Montant/Devise d’abord, puis choix avec/sans email
  const normalGrantAccess = async () => {
    const confirmGrant = window.confirm('Activer ce commerçant sans affiliation ?');
    if (!confirmGrant) return;
    setPendingAction({ type: 'normal' }); // pas d’email encore — on choisira après
    openPaymentModal();                   // devise selon préférences utilisateur
    setShowAffiliateModal(false);         // on laisse la place à la modale paiement
  };

  const handleGrantAccess = () => {
    if (previousAffiliateLink) {
      setReactivationPendingLink(previousAffiliateLink);
      setReactivationModalOpen(true);
      return;
    }
    // Étape 1 : veux-tu attribuer à un affiliate ?
    setModalStep('confirm');
    setShowAffiliateModal(true);
  };

  const handleAffiliateConfirm = (isAffiliate) => {
    if (!isAffiliate) {
      setShowAffiliateModal(false);
      normalGrantAccess();
    } else {
      setModalStep('searchOption');
    }
  };

  const handleSearchOption = async (option) => {
    if (option === 'affiliate') {
      try {
        const codePromoRef = collection(firestore, 'code promo');
        const snapshot = await getDocs(codePromoRef);
        const affiliatesData = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          pseudo: docSnap.data().pseudo || docSnap.id,
          ...docSnap.data()
        }));
        setAffiliates(affiliatesData);
      } catch (error) {
        console.error('Erreur lors de la récupération des affiliés', error);
      }
      setModalStep('selectAffiliate');
    } else {
      try {
        const codePromoRef = collection(firestore, 'code promo');
        const snapshot = await getDocs(codePromoRef);
        const affiliatesData = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          pseudo: docSnap.data().pseudo || docSnap.id,
          ...docSnap.data()
        }));

        const allCodesArray = [];
        for (const aff of affiliatesData) {
          const codesRef = collection(firestore, 'code promo', aff.id, 'codes');
          const codesSnap = await getDocs(codesRef);
          codesSnap.forEach(codeDoc => {
            const d = codeDoc.data();
            allCodesArray.push({
              affiliateId: aff.id,
              affiliatePseudo: aff.pseudo,
              id: codeDoc.id,
              promoCode: d.promoCode,
              discount: d.percentage || 0,
              currency: normalizeCurrency(d.currency || 'EUR'),
            });
          });
        }
        setAllCodes(allCodesArray);
      } catch (error) {
        console.error('Erreur lors de la récupération de tous les codes promo', error);
      }
      setModalStep('selectCode');
    }
  };

  const handleSelectAffiliate = async (e) => {
    const affId = e.target.value;
    const chosenAffiliate = affiliates.find(a => a.id === affId);
    setSelectedAffiliate(chosenAffiliate);

    try {
      const userDocRef = doc(firestore, "users", affId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        setAffiliateEmail(userDocSnap.data().email);
      } else {
        setAffiliateEmail('');
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de l'email de l'affiliate", error);
    }

    try {
      const codesRef = collection(firestore, 'code promo', affId, 'codes');
      const snapshot = await getDocs(codesRef);
      const codesData = snapshot.docs.map(docSnap => {
        const d = docSnap.data();
        return {
          id: docSnap.id,
          promoCode: d.promoCode,
          discount: d.percentage || 0,
          currency: normalizeCurrency(d.currency || 'EUR'),
        };
      });
      const updatedAffiliate = { ...chosenAffiliate, codes: codesData };
      setSelectedAffiliate(updatedAffiliate);
    } catch (error) {
      console.error('Erreur lors de la récupération des codes promo', error);
    }
  };

  const handleAffiliateCodeSelection = (e) => {
    const codeId = e.target.value;
    const codeObj = selectedAffiliate.codes.find(c => c.id === codeId);
    setSelectedCode({
      ...codeObj,
      affiliateId: selectedAffiliate.id,
      affiliatePseudo: selectedAffiliate.pseudo
    });
  };

  const handleDirectCodeSelection = async (e) => {
    const codeId = e.target.value;
    const codeObj = allCodes.find(c => c.id === codeId);
    setSelectedCode(codeObj);
    try {
      const userDocRef = doc(firestore, "users", codeObj.affiliateId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        setAffiliateEmail(userDocSnap.data().email);
      } else {
        setAffiliateEmail('');
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de l'email de l'affiliate", error);
    }
  };

  // Nouvel enchaînement : après avoir choisi le code, on passe d'abord par "Montant/Devise"
  const proceedToAmountStep = () => {
    if (!selectedCode) return;
    setPendingAction({ type: 'affiliate' });
    setShowAffiliateModal(false);
    openPaymentModal(selectedCode?.currency || 'EUR');
  };

  // Étape finale : choisir "avec email" ou "sans email"
  const finalizeWithEmail = (send) => {
    // on respecte STRICTEMENT le choix ici
    setPendingAction(prev => ({ ...(prev || { type: 'normal' }), sendEmail: !!send }));
    confirmPaymentAndProceed();
  };

  const closeReactivationModal = () => {
    setReactivationModalOpen(false);
    setReactivationPendingLink(null);
  };

  const reactivateViaAffiliateLink = async () => {
    if (!reactivationPendingLink) return;
    setReactivating(true);
    try {
      await grantAccessCommon(false);
      await logAffiliateReactivationEntry(reactivationPendingLink);
      const amountInfo = reactivationPendingLink.usedEntry?.recordedAmount
        ? `${formatCurrency(reactivationPendingLink.usedEntry.recordedAmount, reactivationPendingLink.usedEntry.currency)}`
        : '';
      setMessage(
        `Accès réactivé via ${reactivationPendingLink.affiliatePseudo || 'l’affilié'}${amountInfo ? ` (${amountInfo})` : ''}.`
      );
    } catch (error) {
      console.error('Erreur lors de la réactivation via affilié :', error);
      setMessage('Erreur lors de la réactivation via cet affilié.');
    } finally {
      setReactivating(false);
      closeReactivationModal();
    }
  };

  const reactivateWithoutAffiliateLink = async () => {
    if (!reactivationPendingLink) return;
    setReactivating(true);
    try {
      await detachAffiliateLink(reactivationPendingLink);
      await grantAccessCommon(false);
      setMessage('Accès réactivé sans affilié. Transaction précédente supprimée.');
    } catch (error) {
      console.error('Erreur lors de la réactivation sans affilié :', error);
      setMessage('Erreur lors de la réactivation sans affilié.');
    } finally {
      setReactivating(false);
      closeReactivationModal();
    }
  };

  const detachAffiliateLink = useCallback(async (link) => {
    const uid = userDetails?.id || user?.id;
    if (!link || !uid) return;
    try {
      if (link.affiliateId && link.codeId) {
        const codeRef = doc(firestore, 'code promo', link.affiliateId, 'codes', link.codeId);
        // Supprimer depuis la sous-collection activations (nouvelle structure)
        const activSnap = await getDocs(query(collection(codeRef, 'activations'), where('uid', '==', uid)));
        for (const activDoc of activSnap.docs) {
          await deleteDoc(activDoc.ref);
        }
        // Rétro-compat : nettoyer aussi l'ancien tableau usedTimestamps si présent
        const codeSnap = await getDoc(codeRef);
        if (codeSnap.exists()) {
          const currentEntries = normalizeUsedEntries(codeSnap.data().usedTimestamps);
          const filtered = currentEntries.filter(entry => entry?.uid !== uid);
          if (currentEntries.length > 0) {
            if (filtered.length > 0) {
              await updateDoc(codeRef, { usedTimestamps: filtered });
            } else {
              await updateDoc(codeRef, { usedTimestamps: deleteField() });
            }
          }
        }
        await touchAffiliateCodeParent(link.affiliateId);
      }
      await updateDoc(doc(firestore, 'users', uid), {
        codepromo: deleteField(),
      });
      setUserDetails(prev => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next.codepromo;
        return next;
      });
      setPreviousAffiliateLink(null);
    } catch (error) {
      console.error('Erreur lors de la suppression du lien affilié :', error);
      throw error;
    }
  }, [userDetails?.id, user?.id, touchAffiliateCodeParent]);

  const handleResignAccess = async () => {
    const confirmResign = window.confirm('Êtes-vous sûr de vouloir résilier l\'accès de cet utilisateur ?');
    if (confirmResign) {
      try {
        const entrepriseRef = collection(firestore, 'entreprises');
        const qy = query(entrepriseRef, where('userId', '==', user.id));
        const querySnapshot = await getDocs(qy);
        if (!querySnapshot.empty) {
          const entrepriseDoc = querySnapshot.docs[0].ref;
          await updateDoc(entrepriseDoc, {
            base: 'inactive',
            fidelity: 'inactive',
            promotion: 'inactive',
          });
          setMessage('Accès résilié avec succès');
          if (previousAffiliateLink) {
            const now = new Date();
            const updatedEntry = await upsertAffiliateUsageEntry(previousAffiliateLink, (entryData) => ({
              ...entryData,
              status: 'resiliated',
              resiliatedAt: now.toISOString(),
              datetime: entryData.datetime || formatDateTimeForLog(now),
            }));
            if (updatedEntry) {
              setPreviousAffiliateLink(prev => (prev ? { ...prev, usedEntry: updatedEntry } : prev));
            }
          }
        } else {
          setMessage('Aucune entreprise trouvée pour cet utilisateur');
        }
        const updatedUserDoc = await getDoc(doc(firestore, 'users', user.id));
        if (updatedUserDoc.exists()) {
          // 🔥 FIX #2 (partie B) : conserver l'id
          setUserDetails({ id: user.id, ...updatedUserDoc.data() });
        }
      } catch (error) {
        setMessage('Erreur lors de la résiliation de l\'accès');
        console.error(error);
      }
    }
  };

  const handleDeleteUser = async () => {
    const confirmDelete = window.confirm('Êtes-vous sûr de vouloir supprimer ce compte ?');
    if (confirmDelete) {
      try {
        await deleteDoc(doc(firestore, 'users', user.id));
        await fetch('/api/deleteUser', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: user.id }),
        });
        setMessage('Compte supprimé avec succès');
        navigate(-1);
      } catch (error) {
        setMessage('Erreur lors de la suppression du compte');
        console.error(error);
      }
    }
  };

  const handleEditField = (field) => {
    setEditingField(field);
    let currentValue = userDetails[field];
    if (currentValue && typeof currentValue === 'object' && currentValue.seconds) {
      currentValue = new Date(currentValue.seconds * 1000).toLocaleString();
    }
    setEditedValue(currentValue);
  };

  const handleSaveField = async (field) => {
    try {
      const userDocRef = doc(firestore, 'users',  user.id);
      await updateDoc(userDocRef, { [field]: editedValue });
      setUserDetails(prev => ({ ...prev, [field]: editedValue }));
      setEditingField(null);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du champ", error);
    }
  };

  const downloadQRCodeWithBackground = (backgroundUrl, fileName, options = {}) => {
    const {
      qrRatio = 0.55,
      centerYRatio = 0.55,
      captionOffsetRatio = 0.015,
    } = options;
    const finalWidth = 1748, finalHeight = 2480;
    const finalCanvas = document.createElement("canvas");
    finalCanvas.width = finalWidth;
    finalCanvas.height = finalHeight;
    const ctx = finalCanvas.getContext("2d");
    const bgImage = new Image();
    bgImage.crossOrigin = "anonymous";

    bgImage.onload = () => {
      ctx.drawImage(bgImage, 0, 0, finalWidth, finalHeight);
      const qrCanvas = document.getElementById("qr-code-canvas-admin");
      if (qrCanvas) {
        const qrSize = finalWidth * qrRatio;
        const x = (finalWidth - qrSize) / 2;
        const y = finalHeight * centerYRatio - qrSize / 2;
        ctx.drawImage(qrCanvas, x, y, qrSize, qrSize);

        if (shortId) {
          const caption = `ecobill.app/${shortId}`;
          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = '#111';
          const fontSize = Math.round(finalWidth * 0.028);
          ctx.font = `bold ${fontSize}px monospace`;
          const textX = finalWidth / 2;
          const textY = y + qrSize + Math.round(finalHeight * captionOffsetRatio);
          ctx.fillText(caption, textX, textY);
          ctx.restore();
        }
      }
      const dataUrl = finalCanvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = fileName;
      link.click();
    };
    bgImage.src = backgroundUrl;
  };

  const handleDownloadQRCode = () => {
    downloadQRCodeWithBackground(QR_BACKGROUND_STANDARD, "QR-CODE ECOBILL.png");
  };

  const handleDownloadQRCodeNFC = () => {
    downloadQRCodeWithBackground(QR_BACKGROUND_NFC, "QR-CODE ECOBILL NFC.png", {
      qrRatio: 0.275,
      centerYRatio: 0.66,
      captionOffsetRatio: 0.02,
    });
  };

  // Activation / Résiliation pour AFFILIATES
  const handleActivateAffiliate = async () => {
    const ok = window.confirm("Activer ce compte affilié ?");
    if (!ok) return;
    try {
      const uRef = doc(firestore, 'users', user.id);
      await updateDoc(uRef, {
        Status: 'active',
        role: 'affiliates',
        updatedAt: new Date(),
      });

      const fresh = await getDoc(uRef);
      if (fresh.exists()) {
        // 🔥 FIX #2 (partie C) : conserver l'id
        setUserDetails({ id: user.id, ...fresh.data() });
      }

      setMessage("Affilié activé avec succès.");
      setCanActivateAffiliate(false);
      setCanResignAffiliate(true);
    } catch (e) {
      console.error(e);
      setMessage("Erreur lors de l'activation de l'affilié.");
    }
  };

  const handleResignAffiliate = async () => {
    const ok = window.confirm("Résilier ce compte affilié ?");
    if (!ok) return;
    try {
      const uRef = doc(firestore, 'users', user.id);
      await updateDoc(uRef, {
        Status: 'inactive',
        role: 'affiliatepending',
        updatedAt: new Date(),
      });

      const fresh = await getDoc(uRef);
      if (fresh.exists()) {
        // 🔥 FIX #2 (partie D) : conserver l'id
        setUserDetails({ id: user.id, ...fresh.data() });
      }

      setMessage("Affilié résilié avec succès.");
      setCanActivateAffiliate(true);
      setCanResignAffiliate(false);
    } catch (e) {
      console.error(e);
      setMessage("Erreur lors de la résiliation de l'affilié.");
    }
  };

  const QRCodeDisplay = ({ value, caption }) => {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    const internalSize = isMobile ? 500 : 1750;
    const displaySize = isMobile ? 80 : 110; 
    const containerWidth = isMobile ? '150px' : '200px';
    return (
      <div id="qr-container-admin" style={{ position: 'relative', display: 'inline-block', width: containerWidth }}>
        <img
          crossOrigin="anonymous"
          src={QR_BACKGROUND_STANDARD}
          alt="Fond QR Code"
          style={{ display: 'block', width: '100%', height: 'auto' }}
        />
        <div style={{ position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <QRCodeCanvas
              id="qr-code-canvas-admin"
              value={value}
              size={internalSize}
              level="H"
              includeMargin={false}
              bgColor="transparent"
              style={{ width: `${displaySize}px`, height: `${displaySize}px` }}
            />
            {caption ? (
              <div
                style={{
                  marginTop: isMobile ? 4 : 6,
                  fontSize: isMobile ? '9px' : '12px',
                  lineHeight: 1.1,
                  color: '#111',
                  fontFamily: 'var(--eb-font-mono)',
                  whiteSpace: 'nowrap',
                  textAlign: 'center'
                }}
              >
                {caption}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  // ID input file pour import contrat
  const importInputId = userDetails?.id ? `import-contrat-${userDetails.id}` : 'import-contrat';

  // === REGIE: toggle rôle affiliates <-> AffiliateRegie
  const toggleRegieRole = async () => {
    if (!userDetails?.id) return;
    try {
      setUpdatingRegie(true);
      const uRef = doc(firestore, 'users', userDetails.id);
      const nextRole = userDetails.role === 'AffiliateRegie' ? 'affiliates' : 'AffiliateRegie';
      await updateDoc(uRef, { role: nextRole, updatedAt: new Date() });
      // relecture pour MAJ immédiate de l'UI locale
      const fresh = await getDoc(uRef);
      if (fresh.exists()) {
        // 🔥 FIX #2 (partie E) : conserver l'id
        setUserDetails({ id: userDetails.id, ...fresh.data() });
      }
      setMessage(nextRole === 'AffiliateRegie' ? 'Mode Régie activé.' : 'Mode Régie désactivé.');
    } catch (e) {
      console.error('Erreur changement rôle Régie:', e);
      setMessage("Erreur lors du changement de rôle Régie.");
    } finally {
      setUpdatingRegie(false);
    }
  };

  // === REGIE: navigation Voir plus
  const goGestionRegie = () => {
    navigate('/admingestionregie', { state: { from: 'AdminUsersViewer', uid: userDetails?.id } });
  };

  // Redirige vers la création d'un code promo en pré‑sélectionnant cet affilié
  const goCreatePromoForAffiliate = () => {
    if (!userDetails?.id) return;
    navigate('/admincodepromo', {
      state: {
        preselectAffiliateId: userDetails.id,
        preselectAffiliatePseudo: userDetails.pseudo || userDetails.id,
        openForm: true,
        from: 'AdminUsersViewer',
      },
    });
  };

  if (!user) {
    return (
      <div className="dashboard-container">
        <AdminNavbar />
        <div style={{ width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, marginTop: 40 }}>
          <div className="eb-card eb-card--pad" style={{ width: '100%', textAlign: 'center' }}>
            <h1 className="eb-page-title" style={{ fontSize: 20, margin: '0 0 8px', textAlign: 'center' }}>Aucun utilisateur sélectionné</h1>
            <button
              className="eb-btn eb-btn--secondary"
              onClick={() => navigate(-1)}
              style={{ marginTop: 8 }}
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isAffiliateRole = userDetails.role === 'affiliates' || userDetails.role === 'affiliatepending' || userDetails.role === 'AffiliateRegie';

  return (
    <div className="dashboard-container">
      <style>{modalAndBtnStyles}</style>

      <AdminNavbar />

      <div className="auv-page">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Détails de l'utilisateur</h1>
            <p className="eb-page-subtitle">Consultez et modifiez la fiche du compte</p>
          </div>
          <button
            className="eb-btn eb-btn--secondary"
            onClick={() => navigate(-1)}
          >
            ← Retour
          </button>
        </header>

        {/* Identité + raccourcis */}
        <section className="auv-card">
          <div className="auv-identity">
            {profilePhotoUrl && (
              <img
                src={profilePhotoUrl}
                alt="Profil"
                className="auv-avatar"
              />
            )}
            <div className="auv-identity__main">
              <p className="auv-identity__name">{userDetails.pseudo || userDetails.email || userDetails.id}</p>
              <p className="auv-identity__meta">
                {userDetails.role && <span className="eb-badge">{userDetails.role}</span>}
                {userDetails.email && <span>{userDetails.email}</span>}
                <span className="auv-mono">{userDetails.id}</span>
              </p>
            </div>
          </div>

          {/* Boutons Docs + raccourcis affiliés */}
          {isAffiliateRole && (
            <div className="auv-toolbar" style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--eb-border)' }}>
              <button
                className="btn btn-open"
                onClick={() => openDocsModal(userDetails.id)}
                title="Voir les documents (photo, KBIS/INSEE, RIB, Contrat signé)"
              >
                Ouvrir les documents
              </button>

              <input
                id={importInputId}
                type="file"
                accept="application/pdf,image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleImportSignedContract(userDetails.id, e.target.files?.[0], e.currentTarget)}
              />
              <button
                className="btn btn-import"
                onClick={() => document.getElementById(importInputId)?.click()}
                disabled={uploadingUid === userDetails.id}
                title="Importer la version finale cosignée"
              >
                {uploadingUid === userDetails.id ? 'Import…' : 'Importer contrat signé'}
              </button>

              <button
                className="btn"
                onClick={goCreatePromoForAffiliate}
                title="Créer un code promo pour cet affilié"
              >
                Nouveau code promo
              </button>

              {/* === REGIE: bloc switch + Voir plus — visible UNIQUEMENT si role affiliates ou AffiliateRegie */}
              {(userDetails.role === 'affiliates' || userDetails.role === 'AffiliateRegie') && (
                <div className="regie-inline">
                  <div className="switch-wrap">
                    <span className="switch-title">Régie</span>
                    <div
                      role="switch"
                      aria-checked={userDetails.role === 'AffiliateRegie'}
                      className={`toggle ${userDetails.role === 'AffiliateRegie' ? 'checked' : ''}`}
                      onClick={() => { if (!updatingRegie) toggleRegieRole(); }}
                      title={updatingRegie ? 'Mise à jour…' : (userDetails.role === 'AffiliateRegie' ? 'Désactiver Régie' : 'Activer Régie')}
                      style={{ opacity: updatingRegie ? 0.6 : 1, pointerEvents: updatingRegie ? 'none' : 'auto' }}
                    />
                  </div>

                  <button
                    className="btn"
                    onClick={goGestionRegie}
                    title="Voir plus (gestion Régie)"
                  >
                    Voir plus
                  </button>
                </div>
              )}
            </div>
          )}

          {userDetails.role === 'commercant' && (
            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--eb-border)' }}>
              <div className="auv-qr">
                <QRCodeDisplay
                  value={`https://ecobill.app/redirectqrc/${userDetails.id}`}
                  caption={shortId ? `ecobill.app/${shortId}` : ''}
                />
              </div>
              <div className="auv-toolbar" style={{ justifyContent: 'center' }}>
                <button
                  className="eb-btn eb-btn--secondary"
                  onClick={handleDownloadQRCode}
                >
                  QRC régulier
                </button>
                <button
                  className="eb-btn eb-btn--secondary"
                  onClick={handleDownloadQRCodeNFC}
                >
                  QRC NFC
                </button>
                <button
                  className="eb-btn eb-btn--primary"
                  onClick={() => navigate('/adminmerchantgestionviewer', {
                    state: {
                      merchantUid: userDetails.id,
                      merchantName: userDetails.pseudo || userDetails.nom || userDetails.id,
                    }
                  })}
                >
                  Voir fidélité
                </button>
              </div>
            </div>
          )}
        </section>

        {message && <div className="eb-alert eb-alert--info">{message}</div>}
        {previousAffiliateLink && (
          <div className="eb-alert" style={{ background: 'var(--eb-warning-bg)', borderColor: '#fde3b0', color: 'var(--eb-warning)' }}>
            Historique : activation via {previousAffiliateLink.affiliatePseudo || 'un affilié'}
            {previousAffiliateLink.promoCode ? ` (code ${previousAffiliateLink.promoCode})` : ''}.
          </div>
        )}
        {checkingAffiliateHistory && (
          <p className="eb-muted" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 13.5 }}>
            <span className="eb-spinner eb-spinner--sm" /> Vérification du lien affilié précédent...
          </p>
        )}

        <section className="auv-card">
          <h2 className="auv-card__title">Données du compte</h2>
          <div className="user-details auv-fields">
            {Object.entries(userDetails).map(([key, value]) => (
              <div
                key={key}
                className={`auv-field${key === 'codepromo' ? ' auv-field--highlight' : ''}`}
              >
                <div className="auv-field__body">
                  <span className="auv-field__key">{key}</span>
                  <span className="auv-field__val">
                    {editingField === key ? (
                      <input
                        type="text"
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      typeof value === 'object' && value.seconds
                        ? new Date(value.seconds * 1000).toLocaleString()
                        : value?.toString()
                    )}
                  </span>
                </div>
                <div className="auv-field__actions">
                  {editingField === key ? (
                    <>
                      <button className="eb-btn eb-btn--primary eb-btn--sm" onClick={() => handleSaveField(key)}>Sauvegarder</button>
                      <button className="eb-btn eb-btn--secondary eb-btn--sm" onClick={() => setEditingField(null)}>Annuler</button>
                    </>
                  ) : (
                    <button className="eb-btn eb-btn--ghost eb-btn--sm" onClick={() => handleEditField(key)}>Modifier</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="auv-card">
          <h2 className="auv-card__title">Actions sur le compte</h2>
          <div className="user-actions auv-actions">
            {canGrantAccess && (
              <button
                className="button-validation"
                onClick={handleGrantAccess}
                disabled={checkingAffiliateHistory}
                title={checkingAffiliateHistory ? 'Vérification du lien affilié en cours' : undefined}
              >
                Accorder l'accès
              </button>
            )}
            {canResignAccess && (
              <button className="button-classic" onClick={handleResignAccess}>
                Résilier l'accès
              </button>
            )}

            {/* Affiliates */}
            {canActivateAffiliate && (
              <button className="button-validation" onClick={handleActivateAffiliate}>
                Activer l'affilié
              </button>
            )}
            {canResignAffiliate && (
              <button className="button-classic" onClick={handleResignAffiliate}>
                Résilier l'affilié
              </button>
            )}

            <button className="button-delete" onClick={handleDeleteUser}>
              Supprimer le compte
            </button>
          </div>
        </section>
      </div>

      {/* ====== MODALE RÉACTIVATION HISTORIQUE ====== */}
      {reactivationModalOpen && reactivationPendingLink && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button type="button" aria-label="Fermer" className="modal-close" onClick={() => { if (!reactivating) closeReactivationModal(); }}>&times;</button>
            <h2>Réactivation détectée</h2>
            <p>
              Ce commerçant a déjà été activé via {reactivationPendingLink.affiliatePseudo || 'un affilié'}
              {reactivationPendingLink.promoCode ? ` (code ${reactivationPendingLink.promoCode})` : ''}.
            </p>
            {reactivationPendingLink.usedEntry?.recordedAmount && (
              <p className="hint">
                Dernier montant enregistré&nbsp;:
                {' '}
                {formatCurrency(
                  reactivationPendingLink.usedEntry.recordedAmount,
                  reactivationPendingLink.usedEntry.currency || 'EUR'
                )}
              </p>
            )}
            <p>Souhaitez-vous réactiver via le même affilié ?</p>
            <div className="modal-actions">
              <button
                className="btn-primary"
                onClick={reactivateViaAffiliateLink}
                disabled={reactivating}
              >
                Oui, via l'affilié
              </button>
              <button
                className="btn-ghost"
                onClick={reactivateWithoutAffiliateLink}
                disabled={reactivating}
              >
                Oui, sans l'affilié
              </button>
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={closeReactivationModal} disabled={reactivating}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== MODALE AFFILIATION / PARCOURS ====== */}
      {showAffiliateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            {/* Écran gris "moche" de transition */}
            {modalStep === 'confirm' && (
              <div className="ugly-splash">
                <h2>Attribuer à un affiliate ?</h2>
                <div className="modal-actions">
                  <button className="btn-primary" onClick={() => handleAffiliateConfirm(true)}>Oui</button>
                  <button className="btn-ghost" onClick={() => handleAffiliateConfirm(false)}>Non</button>
                </div>
              </div>
            )}

            {modalStep === 'searchOption' && (
              <>
                <h2>Comment souhaitez-vous rechercher ?</h2>
                <div className="modal-actions">
                  <button className="btn-primary" onClick={() => handleSearchOption('affiliate')}>Par affilié</button>
                  <button className="btn-ghost" onClick={() => handleSearchOption('code')}>Par code promo</button>
                </div>
                <div className="modal-actions">
                  <button className="btn-ghost" onClick={() => setShowAffiliateModal(false)}>Annuler</button>
                </div>
              </>
            )}

            {modalStep === 'selectAffiliate' && (
              <>
                <h2>Sélectionnez un affilié</h2>
                <select onChange={handleSelectAffiliate} defaultValue="">
                  <option value="" disabled>-- Choisir un affilié --</option>
                  {affiliates.map(aff => (
                    <option key={aff.id} value={aff.id}>
                      {aff.pseudo || aff.id}
                    </option>
                  ))}
                </select>

                {selectedAffiliate && selectedAffiliate.codes && (
                  <>
                    <h3>Choisissez un code promo :</h3>
                    <select onChange={handleAffiliateCodeSelection} defaultValue="">
                      <option value="" disabled>-- Choisir un code promo --</option>
                      {selectedAffiliate.codes.map(code => (
                        <option key={code.id} value={code.id}>
                          {code.promoCode} ({code.currency})
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <div className="modal-actions">
                  <button 
                    className="btn-primary"
                    onClick={proceedToAmountStep} 
                    disabled={!selectedCode}
                  >
                    Continuer
                  </button>
                  <button className="btn-ghost" onClick={() => setShowAffiliateModal(false)}>
                    Annuler
                  </button>
                </div>
              </>
            )}

            {modalStep === 'selectCode' && (
              <>
                <h2>Sélectionnez un code promo (liste complète)</h2>
                <select onChange={handleDirectCodeSelection} defaultValue="">
                  <option value="" disabled>-- Choisir un code promo --</option>
                  {allCodes.map(code => (
                    <option key={code.id} value={code.id}>
                      {code.promoCode} (Affilié : {code.affiliatePseudo}, {code.currency})
                    </option>
                  ))}
                </select>
                <div className="modal-actions">
                  <button 
                    className="btn-primary"
                    onClick={proceedToAmountStep} 
                    disabled={!selectedCode}
                  >
                    Continuer
                  </button>
                  <button className="btn-ghost" onClick={() => setShowAffiliateModal(false)}>
                    Annuler
                  </button>
                </div>
              </>
            )}

            {modalStep === 'validateChoice' && (
              <>
                <h2>Choisissez votre option de validation</h2>
                <p className="inline-note">
                  Abonnement : <strong>{paymentInterval === 'annuel' ? 'Annuel' : 'Mensuel'}</strong>
                </p>
                <p className="inline-note">
                  {paymentInterval === 'annuel' && annualPreview ? (
                    <>
                      Montant enregistré par mois :{' '}
                      <strong>{formatCurrency(annualPreview.monthlyRecorded, annualPreview.currency)}</strong>
                      {' '} (facteur {factorLabel})
                      <br />
                      Projection 12 mois :{' '}
                      <strong>{formatCurrency(annualPreview.total, annualPreview.currency)}</strong>
                      {' '}(
                      {formatCurrency(annualPreview.firstTwo, annualPreview.currency)} pour 2 mois initiaux
                      {' + '}
                      {annualPreview.recurringMonthsCount} × {formatCurrency(annualPreview.recurringMonthly, annualPreview.currency)} commissions)
                    </>
                  ) : (
                    <>
                      Montant final enregistré :{' '}
                      <strong>{suggestedAmount !== null ? formatCurrency(suggestedAmount, paymentCurrency) : '—'}</strong>
                      {' '} (facteur {factorLabel})
                    </>
                  )}
                </p>
                <div className="modal-actions">
                  <button className="btn-primary" onClick={() => finalizeWithEmail(true)}>
                    Valider avec email
                  </button>
                  <button className="btn-ghost" onClick={() => finalizeWithEmail(false)}>
                    Valider sans email
                  </button>
                </div>
                <div className="modal-actions">
                  <button className="btn-ghost" onClick={() => { setModalStep(validationOrigin || 'confirm'); }}>
                    Retour
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---- Modale documents affilié ---- */}
      {docsModalOpen && (
        <div className="modal-overlay" onClick={closeDocsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" aria-label="Fermer" className="modal-close" onClick={closeDocsModal}>&times;</button>
            <h3>
              Documents — {userDetails ? (userDetails.pseudo || userDetails.id) : (selectedUid || '')}
            </h3>
            {docsLoading ? (
              <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span className="eb-spinner eb-spinner--sm" /> Chargement des documents…</p>
            ) : (
              <div className="docs-grid">
                <div className="doc-block">
                  <strong>Photo de profil</strong>
                  <div style={{ marginTop: 8 }}>
                    {docsPreview.photo ? (
                      <img className="thumb" src={docsPreview.photo} alt="Profil" />
                    ) : (
                      <em>Aucune photo trouvée</em>
                    )}
                  </div>
                </div>

                <div className="doc-block">
                  <strong>Kbis / INSEE</strong>
                  <div className="doc-links" style={{ marginTop: 8 }}>
                    {docsPreview.kbis.length ? docsPreview.kbis.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer">Voir document {i + 1}</a>
                    )) : <em>Aucun document</em>}
                  </div>
                </div>

                <div className="doc-block">
                  <strong>RIB signé</strong>
                  <div className="doc-links" style={{ marginTop: 8 }}>
                    {docsPreview.rib.length ? docsPreview.rib.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer">Voir document {i + 1}</a>
                    )) : <em>Aucun document</em>}
                  </div>
                </div>

                <div className="doc-block">
                  <strong>Contrat signé</strong>
                  <div className="doc-links" style={{ marginTop: 8 }}>
                    {docsPreview.contrat.length ? docsPreview.contrat.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer">Voir document {i + 1}</a>
                    )) : <em>Aucun document</em>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Modale Saisie Montant/Devise ---- */}
      {paymentModalOpen && (
        <div className="modal-overlay" onClick={closePaymentModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" aria-label="Fermer" className="modal-close" onClick={closePaymentModal}>&times;</button>
            <h3>Saisir le montant et la devise</h3>
            <p className="inline-note">
              Selon le code promo, {factorLabel} du montant saisi seront enregistrés.
            </p>

            <div className="row" style={{ marginTop: 10 }}>
              <input
                className="input"
                type="text"
                inputMode="decimal"
                placeholder="Montant (ex. 49.90)"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
              />
              <select
                className="select"
                value={paymentCurrency}
                onChange={(e) => setPaymentCurrency(e.target.value)}
              >
                {ALLOWED_CURRENCIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="interval-select">
              <span style={{ fontWeight: 600 }}>Périodicité de l'abonnement</span>
              <select
                className="select"
                style={{ width: '100%' }}
                value={paymentInterval}
                onChange={(e) => setPaymentInterval(e.target.value)}
              >
                <option value="mensuel">Mensuel</option>
                <option value="annuel">Annuel</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
              <div
                className={`toggle${useFullAmount ? ' checked' : ''}`}
                onClick={() => setUseFullAmount(v => !v)}
              />
              <span style={{ fontSize: 14, color: 'var(--eb-text-2)' }}>
                Enregistrer le plein montant{useFullAmount ? '' : ` (facteur ${factorLabel} appliqué)`}
              </span>
            </div>

            <div className="hint">
              {suggestedAmount !== null ? (
                paymentInterval === 'annuel' && annualPreview ? (
                  <>
                    <strong>
                      Montant enregistré par mois : {formatCurrency(annualPreview.monthlyRecorded, annualPreview.currency)}
                    </strong>
                    <br />
                    Projection 12 mois :{' '}
                    <strong>{formatCurrency(annualPreview.total, annualPreview.currency)}</strong>
                    {' '}(
                    {formatCurrency(annualPreview.firstTwo, annualPreview.currency)} pour 2 mois initiaux
                    {' + '}
                    {annualPreview.recurringMonthsCount} × {formatCurrency(annualPreview.recurringMonthly, annualPreview.currency)} commissions)
                  </>
                ) : (
                  <strong>
                    Montant final enregistré : {formatCurrency(suggestedAmount, paymentCurrency)}{useFullAmount ? '' : ` (facteur ${factorLabel})`}
                  </strong>
                )
              ) : (
                <em>Entrez un montant valide pour voir la suggestion.</em>
              )}
            </div>

            {/* IMPORTANT : ici on ne valide pas encore, on passe au choix "avec/sans email" */}
            <div className="actions">
              <button className="btn-ghost" onClick={closePaymentModal}>Annuler</button>
              <button
                className="btn-primary"
                onClick={continueToValidationChoice}
                disabled={suggestedAmount === null}
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminUsersViewer;
